import { useCallback, useEffect, useRef, useState } from "react"
import { DEFAULT_READ_TIMEOUT_MS, post } from "helpers/api_helper"

export const GENERATION_PATH = "/admin/tournament-proposal-generation"
// A change of the review is read again once the clicks stop for this long.
export const RECOMPUTE_MS = 400

const FAILURES = {
  INVALID_PROPOSAL_QUERY: "The generation was refused: check the period (at most 12 months) and the organizer, then generate again.",
  SUPER_ADMIN_REQUIRED: "Only an active SuperAdmin can generate proposals.",
  ERROR_PERMISSIONS: "Only an active SuperAdmin can generate proposals.",
}
const failureOf = error => FAILURES[error?.response?.data?.message] || "The drafts could not be generated. Try again."

// The review's choices by row (Phase D3): only what the SuperAdmin changed — a tick, a move by whole weeks, the last held edition, the
// competition ticks — merged with what the row had chosen before. A change may be a function of the row's choice so far, so that clicks
// made before the server answers add up.
const merged = (choices, rowId, change) => {
  const before = choices[rowId] || {}
  const made = typeof change === "function" ? change(before) : change
  const next = { ...before, ...made, ...(made.competitions && { competitions: { ...before.competitions, ...made.competitions } }) }
  return { ...choices, [rowId]: next }
}
// A row as the review shows it while its choices wait for the server: the ticks chosen, on the row and on its competitions. Once the answer
// lands, the server's row is shown as it is (it may refuse a tick, as for `datesNotInOrder`).
export const chosenRow = (row, choice) => {
  if (!choice) return row
  const lines = list => list.map(line => (typeof choice.competitions?.[line.id] === "boolean" ? { ...line, ticked: choice.competitions[line.id] } : line))
  return { ...row, ticked: choice.ticked ?? row.ticked, draft: { ...row.draft, competitions: lines(row.draft.competitions), suggested: lines(row.draft.suggested) } }
}

// „Generarea propunerilor” is a synchronous read (ticket 17): one run asks the preview for a country, a period and a scope; each change of
// the review asks it again with the review's choices, since only the server's engine recomputes the drafts, their warnings, suggestions
// and the gaps. Only the latest answer shows; an older one landing late is dropped (CP lesson 2026-09-30).
export default function useGeneration() {
  const [state, setState] = useState({ status: "idle", choices: {} })
  const asked = useRef(0)
  const request = useRef(null)
  const choices = useRef({})
  const timer = useRef(null)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false; clearTimeout(timer.current) }, [])

  const read = useCallback(status => {
    const ask = (asked.current += 1)
    setState(current => ({ ...current, status, error: null }))
    post(GENERATION_PATH, { ...request.current, choices: choices.current }, { timeout: DEFAULT_READ_TIMEOUT_MS })
      .then(response => mounted.current && ask === asked.current && setState(current => ({ ...current, status: "ready", answer: response?.data || null })))
      .catch(error => mounted.current && ask === asked.current && setState(current => ({ ...current, status: "failed", error: failureOf(error) })))
  }, [])
  // A new run starts without choices and without the previous answer.
  const run = useCallback(body => {
    clearTimeout(timer.current)
    request.current = body
    choices.current = {}
    setState({ status: "loading", choices: {} })
    read("loading")
  }, [read])
  // A change makes any read on its way stale: its answer is dropped, and the review waits for the read of the latest choices.
  const choose = useCallback((rowId, change) => {
    asked.current += 1
    choices.current = merged(choices.current, rowId, change)
    setState(current => ({ ...current, status: "updating", choices: choices.current }))
    clearTimeout(timer.current)
    timer.current = setTimeout(() => read("updating"), RECOMPUTE_MS)
  }, [read])
  // A batch refused because some sources were planned meanwhile (D3 `PROPOSAL_SOURCE_ALREADY_PLANNED`): read again with the same choices but
  // those rows' ticks, so that they come back "Already in the calendar"; the moves and the competition ticks stay.
  const untick = useCallback(rowIds => {
    clearTimeout(timer.current)
    choices.current = Object.fromEntries(Object.entries(choices.current).map(([rowId, choice]) => {
      if (!rowIds.includes(rowId)) return [rowId, choice]
      const { ticked, ...rest } = choice
      return [rowId, rest]
    }).filter(([, choice]) => Object.keys(choice).length))
    setState(current => ({ ...current, choices: choices.current }))
    read("updating")
  }, [read])
  // Back to the form: the answer and the choices are dropped.
  const reset = useCallback(() => {
    clearTimeout(timer.current)
    asked.current += 1
    choices.current = {}
    setState({ status: "idle", choices: {} })
  }, [])
  // After a failed read, the same request and choices again.
  const retry = useCallback(() => read("updating"), [read])
  return { ...state, run, choose, untick, reset, retry }
}
